import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Clock, ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import CollapsibleSection from './shared/components/CollapsibleSection';
import CountdownTimer from './features/timeline/components/CountdownTimer';
import ScheduleChart from './features/timeline/components/ScheduleChart';
import TimeElementList from './features/timeline/components/TimeElementList';
import TopNav from './shared/components/TopNav';
import OpenInAppButton from './shared/components/OpenInAppButton';
import { useTimeline } from './features/timeline/context/TimelineContext';
import { useTime } from './shared/context/TimeContext';
import { useRunningState } from './features/timeline/hooks/useRunningState';
import { localizeRunningStateForBrowser } from './features/timeline/utils/runningState';
import timeSyncService from './features/sync/services/timeSyncService';
import {
  parseTimeToSeconds,
  formatTo12Hour,
  calculateOccurrenceStartTimeFromElements,
} from '@thekwallace/backtime-shared';

/**
 * Main App component - renders the timeline UI
 * All state management is handled by TimelineContext
 */
const App: React.FC = () => {
  const { code: publicCode } = useParams<{ code?: string }>();

  const {
    // Loading states
    loading,
    error,
    setError,

    // Current timeline
    currentTimelineId,
    timelineName,
    isTimelinePublic,
    timelinePublicCode,
    isOwner,
    isSubscribed,

    // All timelines
    allTimelines,

    // Schedule
    schedule,
    setSchedule,
    selectedStartTime,
    timeOffset,
    setTimeOffset,
    activeStartTime,
    isAutoAdvanceEnabled,
    setIsAutoAdvanceEnabled,
    autoAdvanceBuffer,
    setAutoAdvanceBuffer,

    // Elements (flat array with sortOrder and anchorOffset)
    elements,

    // Computed values
    scheduledOccurrenceTimes,
    upcomingTimes,
    noUpcomingOccurrences,
    clientAutoAdvanceTime,
    effectiveStartTime,
    effectiveStartAbsolute,
    effectiveDayName,
    isOccurrencePast,

    // Timeline CRUD handlers
    handleTimelineSwitch,
    handleCreateNewTimeline,
    handleDeleteTimeline,
    handleTimelineNameChange,
    handleTogglePublic,
    handlePublicCodeChange,

    // Subscription handlers
    handleSubscribe,
    handleUnsubscribe,

    // Schedule handlers
    handleNextStartTime,
    handlePrevStartTime,

    // Element handlers (new flat structure)
    handleAddElement,
    handleUpdateElement,
    handleDeleteElement,
    handleReorderElements,
    handleMoveElement,
    handleUngroupElement,

    // Defaults handlers
    handleSetDefaults,
    handleLoadDefaults,
  } = useTimeline();

  // Running state (Operate Mode) is owned HERE, not inside TimeElementList, because
  // two components render it: the schedule list AND the hero countdown. Holding it
  // in one place is what stops them contradicting each other — the hero showing
  // planned time while the card below it shows actual was exactly that bug
  // (plan §9.1, "one source of truth for which row is live").
  const {
    runningState,
    pending: runPending,
    error: runError,
    start: startRun,
    mark: markElement,
    clearObservation,
    holdElement,
    undoLast,
    redoLast,
    complete: completeRun,
    abandon: abandonRun,
    clearError: clearRunError,
  } = useRunningState(currentTimelineId);

  // Running state arrives as ISO-8601 INSTANTS (shared 0.9.0, plan §6.1) and is
  // localized into this browser's absolute-seconds frame exactly once, here, next
  // to the hook that owns it. Consumers below receive only the localized form, so
  // no component can accidentally compare an instant-derived number against a
  // planned timing computed from a different midnight.
  //
  // That comparison is precisely the bug 0.9.0 removes: observations used to
  // travel as seconds-from-midnight in the TIMELINE's zone while this app renders
  // in the BROWSER's, so an operator outside the timeline's zone saw every
  // running-state number skewed by the offset — a server variance of 633s
  // rendering as "+3:10:33 behind", and a row reading "Started at 10:57 PM" on a
  // device showing 7:57 PM.
  //
  // Re-derived every tick rather than pinned to the first render: the frame is
  // seconds from the browser's LOCAL midnight, so it shifts by 86400 when the day
  // rolls over. A stale anchor would put a run started before midnight a full day
  // out of step with the clock the same components render against.
  //
  // Anchored on `getSyncedTimeSeconds(timeOffset)`, i.e. the OFFSET clock — the
  // same one `getTimeWithOffset(timeOffset)` gives the consumers as "now". This
  // matters: observations and "now" are subtracted from each other constantly, so
  // they must carry `timeOffset` together or not at all. The planned baseline is
  // the one thing that must NOT carry it — the schedule does not move when the
  // operator nudges their clock — and `runPlannedBaselineSeconds` takes it back
  // off at that single derivation. See its comment for why both halves are
  // needed; getting only one right makes `timeOffset` a silent no-op for running
  // state while the server keeps honouring it.
  const { currentTimeMs } = useTime();
  const localizedRunningState = useMemo(
    () => localizeRunningStateForBrowser(
      runningState,
      timeSyncService.getSyncedTimeSeconds(timeOffset),
      timeSyncService.getSyncedTimeMs()
    ),
    // currentTimeMs is a tick, not a value we read — it is what re-anchors the
    // conversion across midnight. eslint cannot see that from the body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runningState, currentTimeMs, timeOffset]
  );

  // Mid-run, the Occurrence bar is not a picker — it is a statement of which
  // occurrence is being operated. Anything that could MOVE that selection under a live
  // run is hazardous: auto-advance rolls `activeStartTime` to the next occurrence
  // shortly after this one's planned end, i.e. while an over-running service is still
  // being operated (plan §9.1).
  const isLiveRun = runningState?.status === 'running';

  const formatTimeWithoutZeroSeconds = (time24H: string): string => {
    const formatted = formatTo12Hour(parseTimeToSeconds(time24H), true);
    return formatted.replace(/:00 (AM|PM)$/, ' $1');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-gray-900 dark:via-slate-900 dark:to-gray-800 text-gray-900 dark:text-white">
      {loading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <span className="text-gray-900 dark:text-white">Loading...</span>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed top-0 left-0 right-0 bg-red-600 text-white px-4 py-3 z-50 flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-white hover:text-red-200 font-bold text-xl"
          >
            ×
          </button>
        </div>
      )}

      <TopNav
        timelineName={timelineName}
        onTimelineNameChange={handleTimelineNameChange}
        isPublic={isTimelinePublic}
        onTogglePublic={isOwner ? handleTogglePublic : undefined}
        publicCode={timelinePublicCode ?? undefined}
        onPublicCodeChange={handlePublicCodeChange}
        onSetDefaults={handleSetDefaults}
        onLoadDefaults={handleLoadDefaults}
        isOwner={isOwner}
        timelineData={{ schedule, elements }}
        timelineId={currentTimelineId ?? undefined}
        allTimelines={allTimelines}
        currentTimelineId={currentTimelineId}
        onTimelineSwitch={handleTimelineSwitch}
        onCreateNewTimeline={handleCreateNewTimeline}
        onDeleteTimeline={handleDeleteTimeline}
        canDelete={allTimelines.length > 1}
        isAutoAdvanceEnabled={isAutoAdvanceEnabled}
        onAutoAdvanceChange={setIsAutoAdvanceEnabled}
        autoAdvanceBuffer={autoAdvanceBuffer}
        onAutoAdvanceBufferChange={setAutoAdvanceBuffer}
        timeOffset={timeOffset}
        onTimeOffsetChange={setTimeOffset}
        onUnsubscribe={handleUnsubscribe}
        onSubscribe={handleSubscribe}
        isSubscribed={isSubscribed}
      />

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {publicCode && (
          <div className="mb-6 max-w-md mx-auto">
            <OpenInAppButton publicCode={publicCode} />
          </div>
        )}

        <div className="mb-10">
          <CountdownTimer
            selectedStartTime={effectiveStartTime}
            occurrenceStartAbsolute={effectiveStartAbsolute}
            elements={elements}
            runningState={localizedRunningState}
            timeOffset={timeOffset}
            onTimeOffsetChange={setTimeOffset}
            isPublic={isTimelinePublic}
            publicCode={timelinePublicCode}
            noUpcomingOccurrences={noUpcomingOccurrences}
            isOccurrenceComplete={isOccurrencePast}
            dayName={effectiveDayName}
          />
        </div>

        <div className="mb-8">
          <CollapsibleSection
            title="Times"
            icon={<Clock className="w-6 h-6 text-blue-400" />}
            defaultOpen={false}
            titleAction={
              isOwner ? (
                <button
                  disabled={isLiveRun}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isLiveRun) return;
                    if (isAutoAdvanceEnabled) {
                      const currentEffective = activeStartTime || clientAutoAdvanceTime || scheduledOccurrenceTimes[0]?.time;
                      if (currentEffective) {
                        // This will trigger through context's selectedStartTime setter
                        handleNextStartTime();
                        handlePrevStartTime();
                      }
                    }
                    setIsAutoAdvanceEnabled(!isAutoAdvanceEnabled);
                  }}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors ${
                    isLiveRun ? 'opacity-50 cursor-not-allowed ' : ''
                  }${
                    isAutoAdvanceEnabled
                      ? 'bg-blue-600/30 border border-blue-500/50 text-blue-600 dark:text-blue-400' + (isLiveRun ? '' : ' hover:bg-blue-600/50')
                      : 'bg-gray-200/50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 text-gray-500' + (isLiveRun ? '' : ' hover:bg-gray-300/50 dark:hover:bg-gray-600/50')
                  }`}
                  title={
                    isLiveRun
                      ? 'Locked while an occurrence is running — changing this would move the selection under the run'
                      : isAutoAdvanceEnabled
                        ? 'Click to disable auto-advance'
                        : 'Click to enable auto-advance'
                  }
                >
                  <Zap className="w-3 h-3" />
                  <span>{isAutoAdvanceEnabled ? 'Auto' : 'Auto Off'}</span>
                </button>
              ) : (
                <div
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                    isAutoAdvanceEnabled
                      ? 'bg-blue-600/20 border border-blue-500/30 text-blue-500 dark:text-blue-400'
                      : 'bg-gray-200/30 dark:bg-gray-700/30 border border-gray-300 dark:border-gray-600 text-gray-400'
                  }`}
                  title={isAutoAdvanceEnabled ? 'Auto-advance is enabled' : 'Auto-advance is disabled'}
                >
                  <Zap className="w-3 h-3" />
                  <span>{isAutoAdvanceEnabled ? 'Auto' : 'Auto Off'}</span>
                </div>
              )
            }
            headerContent={
              noUpcomingOccurrences && !isOccurrencePast ? (
                <div className="text-sm text-gray-500 italic">
                  No upcoming times
                </div>
              ) : (
                <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0 flex-shrink">
                  {!isAutoAdvanceEnabled && (
                    <button
                      onClick={handlePrevStartTime}
                      className="flex items-center gap-2 p-1.5 sm:p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/50 dark:hover:bg-gray-700/50 rounded-full transition-all duration-300 transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={upcomingTimes.length <= 1}
                    >
                      {upcomingTimes.length > 1 && (() => {
                        const currentIndex = upcomingTimes.findIndex(time => time === selectedStartTime);
                        const prevIndex = currentIndex === -1
                          ? upcomingTimes.length - 1
                          : (currentIndex - 1 + upcomingTimes.length) % upcomingTimes.length;
                        return (
                          <span className="text-xs text-gray-500">
                            {formatTimeWithoutZeroSeconds(upcomingTimes[prevIndex])}
                          </span>
                        );
                      })()}
                      <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  )}

                  {isLiveRun ? (
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
                        Operating
                      </span>
                      <span className="text-lg sm:text-xl md:text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight whitespace-nowrap">
                        {effectiveStartTime && formatTo12Hour(parseTimeToSeconds(effectiveStartTime), true)}
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-2 md:gap-3 min-w-0">
                      <div className="text-lg sm:text-xl md:text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight whitespace-nowrap">
                        {effectiveStartTime && formatTo12Hour(
                          calculateOccurrenceStartTimeFromElements(parseTimeToSeconds(effectiveStartTime), elements),
                          true
                        )}
                      </div>
                      <div className="w-full sm:w-auto sm:hidden border-t border-gray-300 dark:border-gray-600"></div>
                      <div className="hidden sm:block text-lg sm:text-xl md:text-2xl text-gray-500">/</div>
                      <div className="text-base sm:text-lg md:text-xl text-gray-500 whitespace-nowrap">
                        {effectiveStartTime && formatTo12Hour(parseTimeToSeconds(effectiveStartTime), true)}
                      </div>
                    </div>
                  )}

                  {!isAutoAdvanceEnabled && (
                    <button
                      onClick={handleNextStartTime}
                      className="flex items-center gap-2 p-1.5 sm:p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/50 dark:hover:bg-gray-700/50 rounded-full transition-all duration-300 transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={upcomingTimes.length <= 1}
                    >
                      <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                      {upcomingTimes.length > 1 && (() => {
                        const currentIndex = upcomingTimes.findIndex(time => time === selectedStartTime);
                        const nextIndex = currentIndex === -1
                          ? 0
                          : (currentIndex + 1) % upcomingTimes.length;
                        return (
                          <span className="text-xs text-gray-500">
                            {formatTimeWithoutZeroSeconds(upcomingTimes[nextIndex])}
                          </span>
                        );
                      })()}
                    </button>
                  )}
                </div>
              )
            }
          >
            <ScheduleChart
              schedule={schedule}
              onScheduleChange={setSchedule}
              snapMinutes={30}
              readOnly={!isOwner}
            />

            {isOwner && isAutoAdvanceEnabled && (
              <div className="mt-4 flex items-center justify-between bg-gray-100/50 dark:bg-gray-800/50 rounded-lg p-3">
                <div className="flex-1">
                  <label className="text-sm text-gray-600 dark:text-gray-300">Buffer after occurrence ends</label>
                  <p className="text-xs text-gray-500">Time before switching to next occurrence</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="60"
                    value={Math.round(autoAdvanceBuffer / 60)}
                    onChange={(e) => setAutoAdvanceBuffer(Math.max(0, parseInt(e.target.value) || 0) * 60)}
                    className="w-16 px-2 py-1 text-sm bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">min</span>
                </div>
              </div>
            )}
          </CollapsibleSection>
        </div>

        <div className="mb-8">
          <TimeElementList
            elements={elements}
            selectedStartTime={effectiveStartTime}
            occurrenceStartAbsolute={effectiveStartAbsolute}
            timeOffset={timeOffset}
            onAddElement={handleAddElement}
            onUpdateElement={handleUpdateElement}
            onDeleteElement={handleDeleteElement}
            onReorderElements={handleReorderElements}
            onMoveElement={handleMoveElement}
            onUngroupElement={handleUngroupElement}
            timelineId={currentTimelineId}
            noUpcomingOccurrences={noUpcomingOccurrences}
            isOccurrenceComplete={isOccurrencePast}
            dayName={effectiveDayName}
            readOnly={!isOwner}
            runningState={localizedRunningState}
            runPending={runPending}
            runError={runError}
            onStartRun={startRun}
            onMarkElement={markElement}
            onClearObservation={clearObservation}
            onHoldElement={holdElement}
            onUndoLastObservation={undoLast}
            onRedoLastObservation={redoLast}
            onCompleteRun={completeRun}
            onAbandonRun={abandonRun}
            onClearRunError={clearRunError}
          />
        </div>

        <div className="text-center text-xs text-gray-600 mt-8 pb-4">
          {process.env.REACT_APP_GIT_COMMIT && (
            <span>v{process.env.REACT_APP_GIT_COMMIT}</span>
          )}
          {process.env.REACT_APP_GIT_BRANCH && process.env.REACT_APP_GIT_BRANCH !== 'HEAD' && (
            <span> ({process.env.REACT_APP_GIT_BRANCH})</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default App;
